import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Label } from './ui/label';
import { 
  Building2, 
  ArrowLeft, 
  CheckCircle, 
  Clock, 
  Lock, 
  XCircle, 
  MessageSquare, 
  Calendar,
  DollarSign,
  MapPin,
  FileText,
  User,
  Loader2
} from 'lucide-react';
import { requestsAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';

const RequestDetails = ({ user }) => {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [isRejectionDialogOpen, setIsRejectionDialogOpen] = useState(false);

  useEffect(() => {
    fetchRequest();
  }, [requestId]);

  const fetchRequest = async () => {
    try {
      setLoading(true);
      const data = await requestsAPI.getRequest(requestId);
      setRequest(data);
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to fetch request",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const getStepIcon = (status) => {
    switch (status) {
      case 'approved':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'pending':
        return <Clock className="h-5 w-5 text-yellow-600" />;
      case 'requires_changes':
        return <XCircle className="h-5 w-5 text-orange-600" />;
      case 'rejected':
        return <XCircle className="h-5 w-5 text-red-600" />;
      default:
        return <Lock className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStepColor = (status) => {
    switch (status) {
      case 'approved':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'pending':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'requires_changes':
        return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'rejected':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-gray-400 bg-gray-50 border-gray-200';
    }
  };

  const handleApprove = async () => {
    setActionLoading(true);
    try {
      const action = {
        action: 'approve',
        comment: 'Approved'
      };
      
      await requestsAPI.approveRequest(requestId, action, user.id);
      
      toast({
        title: "Request Approved",
        description: "The request has been approved and moved to the next step",
        variant: "default"
      });

      navigate('/dashboard');
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to approve request",
        variant: "destructive"
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      toast({
        title: "Rejection Reason Required",
        description: "Please provide a reason for rejection",
        variant: "destructive"
      });
      return;
    }

    setActionLoading(true);
    try {
      const action = {
        action: 'reject',
        comment: rejectionReason
      };
      
      await requestsAPI.approveRequest(requestId, action, user.id);
      
      toast({
        title: "Request Rejected",
        description: "The request has been rejected",
        variant: "destructive"
      });

      setIsRejectionDialogOpen(false);
      navigate('/dashboard');
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to reject request",
        variant: "destructive"
      });
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-100 flex items-center justify-center">
        <div className="flex items-center space-x-2">
          <Loader2 className="h-6 w-6 animate-spin text-red-600" />
          <p className="text-lg text-gray-600">Loading request details...</p>
        </div>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-100 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Request Not Found</h2>
          <p className="text-gray-600 mb-4">The requested activity request could not be found.</p>
          <Button onClick={() => navigate('/dashboard')}>Back to Dashboard</Button>
        </div>
      </div>
    );
  }

  const canApprove = user.role !== 'Project Owner' && request.current_approver === user.id;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/dashboard')}
                className="flex items-center space-x-2"
              >
                <ArrowLeft className="h-4 w-4" />
                <span>Back to Dashboard</span>
              </Button>
              <Separator orientation="vertical" className="h-6" />
              {/* Menarini Logo */}
              <div className="bg-red-600 text-white px-2 py-1 text-sm font-bold">
                MENARINI
              </div>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">Request Details</h1>
                <p className="text-sm text-gray-500">{request.id}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <p className="text-sm text-gray-600">Logged in as {user.name}</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* Request Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>{request.title}</span>
              <Badge className={
                request.status === 'pending_approval' ? 'bg-yellow-100 text-yellow-800 border-yellow-300' :
                request.status === 'requires_changes' ? 'bg-orange-100 text-orange-800 border-orange-300' :
                request.status === 'approved' ? 'bg-green-100 text-green-800 border-green-300' :
                'bg-red-100 text-red-800 border-red-300'
              }>
                {request.status.replace('_', ' ').toUpperCase()}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="flex items-center space-x-3">
                <FileText className="h-5 w-5 text-blue-600" />
                <div>
                  <p className="text-sm text-gray-600">Activity Type</p>
                  <p className="font-medium">{request.type}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <MapPin className="h-5 w-5 text-blue-600" />
                <div>
                  <p className="text-sm text-gray-600">Country</p>
                  <p className="font-medium">{request.country}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Calendar className="h-5 w-5 text-blue-600" />
                <div>
                  <p className="text-sm text-gray-600">Event Date</p>
                  <p className="font-medium">{formatDate(request.event_date)}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <User className="h-5 w-5 text-blue-600" />
                <div>
                  <p className="text-sm text-gray-600">Submitted By</p>
                  <p className="font-medium">{request.submitted_by}</p>
                </div>
              </div>
            </div>

            <Separator />

            <div>
              <h3 className="text-lg font-medium mb-3">Business Objective</h3>
              <p className="text-gray-700 leading-relaxed">{request.business_objective}</p>
            </div>

            <Separator />

            <div>
              <h3 className="text-lg font-medium mb-3 flex items-center space-x-2">
                <DollarSign className="h-5 w-5" />
                <span>Financial Breakdown</span>
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">Venue Costs</p>
                  <p className="text-lg font-semibold">{formatCurrency(request.venue_costs)}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">Speaker Fees</p>
                  <p className="text-lg font-semibold">{formatCurrency(request.speaker_fees)}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">Travel Costs</p>
                  <p className="text-lg font-semibold">{formatCurrency(request.travel_costs)}</p>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-600">Total FMV</p>
                  <p className="text-lg font-semibold text-blue-900">{formatCurrency(request.automated_fmv)}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Approval Workflow */}
        <Card>
          <CardHeader>
            <CardTitle>Approval Workflow</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {request.workflow_steps.map((step, index) => (
                <div key={step.step} className="flex items-start space-x-4">
                  <div className={`flex-shrink-0 w-10 h-10 rounded-full border-2 flex items-center justify-center ${getStepColor(step.status)}`}>
                    {getStepIcon(step.status)}
                  </div>
                  <div className="flex-grow">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium">{step.name}</h4>
                        <p className="text-sm text-gray-600">
                          {step.status === 'pending' && `Assigned to: ${step.approver}`}
                          {step.status === 'approved' && `Approved by ${step.approver} on ${formatDate(step.completed_date)}`}
                          {step.status === 'requires_changes' && `Changes requested by ${step.approver} on ${formatDate(step.completed_date)}`}
                          {step.status === 'rejected' && `Rejected by ${step.approver} on ${formatDate(step.completed_date)}`}
                          {step.status === 'locked' && 'Pending previous approval'}
                        </p>
                        {step.comment && (
                          <p className="text-sm text-gray-700 mt-1 italic">"{step.comment}"</p>
                        )}
                      </div>
                      <div className="text-right">
                        <Badge variant="outline" className={getStepColor(step.status)}>
                          {step.status.replace('_', ' ').toUpperCase()}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Comments/History */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <MessageSquare className="h-5 w-5" />
              <span>Comments & History</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {request.comments.map((comment) => (
                <div key={comment.id} className="border-l-4 border-blue-200 pl-4 py-2">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium">{comment.author}</span>
                    <span className="text-sm text-gray-500">{formatDate(comment.date)}</span>
                  </div>
                  <p className="text-sm text-gray-600 mb-1">{comment.author_role}</p>
                  <p className="text-gray-700">{comment.comment}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        {canApprove && (
          <Card>
            <CardContent className="pt-6">
              <div className="flex justify-center space-x-4">
                <Button
                  onClick={handleApprove}
                  className="bg-green-600 hover:bg-green-700 text-white px-8 py-2"
                  disabled={actionLoading}
                >
                  {actionLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                  Approve
                </Button>
                <Dialog open={isRejectionDialogOpen} onOpenChange={setIsRejectionDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="destructive" className="px-8 py-2" disabled={actionLoading}>
                      <XCircle className="h-4 w-4 mr-2" />
                      Reject
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Reject Request</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="rejectionReason">Reason for Rejection *</Label>
                        <Textarea
                          id="rejectionReason"
                          value={rejectionReason}
                          onChange={(e) => setRejectionReason(e.target.value)}
                          placeholder="Please provide a detailed reason for rejecting this request"
                          rows={4}
                        />
                      </div>
                      <div className="flex justify-end space-x-2">
                        <Button variant="outline" onClick={() => setIsRejectionDialogOpen(false)}>
                          Cancel
                        </Button>
                        <Button variant="destructive" onClick={handleReject} disabled={actionLoading}>
                          {actionLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                          Reject Request
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default RequestDetails;